import React from 'react';
import { Form } from '@dashboard/components/Formik';
import usePage from '@dashboard/hooks/usePage';
import { FormElement, Label } from '@dashboard/components/Form';
import Dialog from '@dashboard/components/Dialog';

export default function LayoutForm() {
  const { page, updatePage } = usePage();

  return (
    <>
      <FormElement>
        <Label>
          <span className="font-normal">
            {page.usageType === 'hosted' ? 'Hosted page' : 'Add to site'}
          </span>{' '}
          -{' '}
          <Dialog
            title={`Change page`}
            cancelButton={null}
            confirmButton={null}
            trigger={<button className="font-semibold underline">change</button>}
            size="2xl"
          >
            {({ close }) => (
              <Form
                initialValues={{
                  usageType: page.usageType,
                  closePopupOnClickOutside: page.closePopupOnClickOutside,
                }}
                fields={[
                  {
                    type: 'radioCard',
                    name: 'usageType',
                    columns: 2,
                    options: [
                      {
                        value: 'hosted',
                        label: 'Hosted page',
                        labelDescription: `Link to a hosted ${page.type} page`,
                      },
                      {
                        value: 'site',
                        label: 'Add to site',
                        labelDescription: `Add ${page.type} to your site`,
                      },
                    ],
                  },
                ]}
                onSubmit={async values => {
                  const { data, error } = await updatePage.mutateAsync(values);

                  if (data) {
                    close();
                  }

                  return {
                    data,
                    error,
                  };
                }}
              />
            )}
          </Dialog>
        </Label>
      </FormElement>

      <Form
        autosave
        showState={false}
        initialValues={{
          hostedLayoutType: page.hostedLayoutType || 'center',
          siteLayoutType: page.siteLayoutType || 'embed',
          closePopupOnClickOutside: page.closePopupOnClickOutside || false,
          formPosition: page.formPosition || 'left',
          textAlign: page.textAlign || 'left',
          logoAlign: page.logoAlign || 'left',
          background: page.background || 'color',
          backgroundImage: page.backgroundImage,
          backgroundWidth: page.backgroundWidth,
        }}
        fields={({ values }) => [
          {
            type: 'radioIconCard',
            name: 'siteLayoutType',
            columns: 4,
            options: [
              {
                value: 'embed',
                label: 'Embed',
                component: SplitCard,
                center: true,
              },
              {
                value: 'popup',
                label: 'Pop-up',
                component: CenterCard,
                center: true,
              },
            ],
            hidden: page.usageType !== 'site',
          },
          {
            type: 'radioIconCard',
            name: 'hostedLayoutType',
            columns: 4,
            hidden: page.usageType !== 'hosted',
            options: [
              {
                value: 'center',
                component: CenterCard,
                label: 'Center',
                center: true,
              },
              {
                value: 'double',
                component: DoubleCard,
                label: 'Double',
                center: true,
              },
              {
                value: 'split',
                component: SplitCard,
                label: 'Split',
                center: true,
              },
              /*
            {
              value: 'double_banner',
              component: DoubleBannerCard,
              label: 'Double banner',
            },*/
            ],
          },
          {
            type: 'checkbox',
            name: 'closePopupOnClickOutside',
            label: 'Click outside the pop-up to close',
            visible: values => page.usageType !== 'hosted' && values.siteLayoutType === 'popup',
          },
          {
            type: 'radio',
            name: 'formPosition',
            label: page.type === 'form' ? 'Form position' : 'Checkout position',
            options: [
              {
                value: 'right',
                label: 'Right',
              },
              {
                value: 'left',
                label: 'Left',
              },
            ],
            hidden: page.usageType !== 'hosted' || values.hostedLayoutType === 'center',
          },
          {
            type: 'radio',
            name: 'background',
            label: 'Background',
            options: [
              {
                value: 'color',
                label: 'Color',
              },
              {
                value: 'image',
                label: 'Image',
              },
            ],
            hidden:
              page.usageType !== 'hosted' ||
              values.hostedLayoutType === 'double' ||
              values.hostedLayoutType === 'double_banner',
          },
          {
            type: 'radio',
            name: 'backgroundWidth',
            label: 'Background width',
            options: [
              {
                value: 'half',
                label: 'Half',
              },
              {
                value: 'full',
                label: 'Full',
              },
            ],
            hidden:
              page.usageType !== 'hosted' ||
              values.hostedLayoutType === 'split' ||
              values.hostedLayoutType === 'center',
          },
          {
            type: 'image',
            name: 'backgroundImage',
            label: 'Background image',
            image: values.backgroundImage,
            previewSize: 'background',
            hidden:
              page.usageType !== 'hosted' ||
              values.background !== 'image' ||
              values.hostedLayoutType === 'double' ||
              values.hostedLayoutType === 'double_banner',
          },
          {
            type: 'radio',
            name: 'textAlign',
            label: 'Text alignment',
            options: [
              {
                value: 'left',
                label: 'Left',
                id: 'text-align-left',
              },
              {
                value: 'center',
                label: 'Center',
                id: 'text-align-center',
              },
            ],
          },
          {
            type: 'radio',
            name: 'logoAlign',
            label: 'Logo alignment',
            options: [
              {
                value: 'left',
                label: 'Left',
              },
              {
                value: 'center',
                label: 'Center',
              },
            ],
            hidden: page.usageType !== 'hosted' || values.hostedLayoutType === 'center',
          },
        ]}
        onSubmit={async values => {
          const { data, error } = await updatePage.mutateAsync(values);

          return {
            data,
            error,
          };
        }}
      />
    </>
  );
}

function RadioIconCard({ children }) {
  return (
    <div className="flex items-center text-center justify-center w-full h-auto mb-0">
      {children}
    </div>
  );
}

/* function DoubleBannerCard() {
  return (
    <RadioIconCard>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 100 64"
        width="auto"
        height="64"
      >
        <rect
          width="48"
          height="36"
          y="28"
          fill="#000"
          fillOpacity=".08"
          rx="2"
        />
        <rect
          width="48"
          height="36"
          x="52"
          y="28"
          fill="#000"
          fillOpacity=".08"
          rx="2"
        />
        <rect width="100" height="24" fill="#000" fillOpacity=".08" rx="2" />
      </svg>
    </RadioIconCard>
  );
} */

export function DoubleCard() {
  return (
    <RadioIconCard>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 100 64"
        width="auto"
        height="64"
      >
        <rect width="48" height="64" fill="#000" fillOpacity=".08" rx="2" />
        <rect width="48" height="64" x="52" fill="#000" fillOpacity=".08" rx="2" />
      </svg>
    </RadioIconCard>
  );
}

export function CenterCard() {
  return (
    <RadioIconCard>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 48 64"
        width="auto"
        height="64"
      >
        <rect width="48" height="64" fill="#000" fillOpacity=".08" rx="2" />
      </svg>
    </RadioIconCard>
  );
}

export function SplitCard() {
  return (
    <RadioIconCard>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 100 64"
        width="auto"
        height="64"
      >
        <rect x="52" width="48" height="64" fill="#000" fillOpacity=".08" rx="2" />
      </svg>
    </RadioIconCard>
  );
}
